import React from 'react';
import { FiChevronLeft, FiChevronRight, FiCheck } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import CountryPhoneInput from '../../../components/CountryPhoneInput';
import { steps, statusOptions, approvalOptions, riskRatingOptions } from '../../../context/AdminAssessmentContext';

const AssessmentFormModal = ({
  isOpen,
  onClose,
  isEditing,
  selectedAssessment,
  formData,
  validationErrors,
  currentStep,
  organizations,
  vendors,
  projects,
  categories,
  domains,
  departments,
  disciplines,
  focusAreas,
  industries,
  onInputChange,
  onPhoneChange,
  onNext,
  onPrevious,
  onSubmit,
  onStepChange,
}) => {
  const labelClass = `block text-sm font-medium mb-2 text-[#53545a]`;
  const getInputClass = (fieldName) => 
    `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
      validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'
    } bg-white text-[#53545a]`;
  const getSelectClass = (fieldName) => 
    `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
      validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'
    } bg-white text-[#53545a]`;
  const renderFieldError = (fieldName) => 
    validationErrors[fieldName] ? <p className="text-[#F47F2E] text-xs mt-1">{validationErrors[fieldName]}</p> : null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Account & Vendor
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Organization / Account *</label>
              <select name="accountId" value={formData.accountId} onChange={onInputChange} className={getSelectClass('accountId')}>
                <option value="">Select Organization</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              {renderFieldError('accountId')}
            </div>
            <div>
              <label className={labelClass}>Vendor *</label>
              <select name="vendorId" value={formData.vendorId} onChange={onInputChange} className={getSelectClass('vendorId')}>
                <option value="">Select Vendor</option>
                {vendors.map(v => (
                  <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                ))}
              </select>
              {renderFieldError('vendorId')}
            </div>
            <div>
              <label className={labelClass}>Project *</label>
              <select name="projectId" value={formData.projectId} onChange={onInputChange} className={getSelectClass('projectId')}>
                <option value="">Select Project</option>
                {projects.map(p => (
                  <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
                ))}
              </select>
              {renderFieldError('projectId')}
            </div>
            <div>
              <label className={labelClass}>Industry *</label>
              <select name="industryId" value={formData.industryId} onChange={onInputChange} className={getSelectClass('industryId')}>
                <option value="">Select Industry</option>
                {industries.map(i => (
                  <option key={i.industryId} value={i.industryId}>{i.industryName}</option>
                ))}
              </select>
              {renderFieldError('industryId')}
            </div>
          </div>
        );

      case 1: // Ownership
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Customer Owner *</label>
              <input type="text" name="assessmentCustomerOwner" value={formData.assessmentCustomerOwner} onChange={onInputChange} className={getInputClass('assessmentCustomerOwner')} placeholder="Enter customer owner" />
              {renderFieldError('assessmentCustomerOwner')}
            </div>
            <div>
              <label className={labelClass}>TPRM Owner *</label>
              <input type="text" name="assessmentTprmOwner" value={formData.assessmentTprmOwner} onChange={onInputChange} className={getInputClass('assessmentTprmOwner')} placeholder="Enter TPRM owner" />
              {renderFieldError('assessmentTprmOwner')}
            </div>
            <div>
              <label className={labelClass}>Sponsor *</label>
              <input type="text" name="assessmentSponsor" value={formData.assessmentSponsor} onChange={onInputChange} className={getInputClass('assessmentSponsor')} placeholder="Enter sponsor" />
              {renderFieldError('assessmentSponsor')}
            </div>
            <div>
              <label className={labelClass}>Assessment Category *</label>
              <select name="assessmentCategoryId" value={formData.assessmentCategoryId} onChange={onInputChange} className={getSelectClass('assessmentCategoryId')}>
                <option value="">Select Category</option>
                {categories.map(c => (
                  <option key={c.assessmentCategoryId} value={c.assessmentCategoryId}>{c.name}</option>
                ))}
              </select>
              {renderFieldError('assessmentCategoryId')}
            </div>
            <div>
              <label className={labelClass}>Domain *</label>
              <select name="assessmentDomainId" value={formData.assessmentDomainId} onChange={onInputChange} className={getSelectClass('assessmentDomainId')}>
                <option value="">Select Domain</option>
                {domains.map(d => (
                  <option key={d.domainId} value={d.domainId}>{d.domainName}</option>
                ))}
              </select>
              {renderFieldError('assessmentDomainId')}
            </div>
          </div>
        );

      case 2: // Contact Info
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="col-span-2">
              <h4 className={`text-sm font-semibold mb-3 text-[#53545a]`}>Primary Contact</h4>
            </div>
            <div>
              <label className={labelClass}>Contact Name *</label>
              <input type="text" name="assessmentPrimaryContact" value={formData.assessmentPrimaryContact} onChange={onInputChange} className={getInputClass('assessmentPrimaryContact')} placeholder="Enter primary contact" />
              {renderFieldError('assessmentPrimaryContact')}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" name="assessmentPrimaryEmail" value={formData.assessmentPrimaryEmail} onChange={onInputChange} className={getInputClass('assessmentPrimaryEmail')} placeholder="primary@email.com" />
              {renderFieldError('assessmentPrimaryEmail')}
            </div>
            <div>
              <CountryPhoneInput
                name="assessmentPrimaryPhone"
                value={formData.assessmentPrimaryPhone}
                countryCodeValue={formData.assessmentPrimaryPhoneCountry}
                onChange={onPhoneChange}
                error={validationErrors.assessmentPrimaryPhone}
                required
                label="Phone *"
              />
            </div>
            <div className="col-span-2 mt-4">
              <h4 className={`text-sm font-semibold mb-3 text-[#53545a]`}>Backup Contact</h4>
            </div>
            <div>
              <label className={labelClass}>Contact Name *</label>
              <input type="text" name="assessmentBackupContact" value={formData.assessmentBackupContact} onChange={onInputChange} className={getInputClass('assessmentBackupContact')} placeholder="Enter backup contact" />
              {renderFieldError('assessmentBackupContact')}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" name="assessmentBackupEmail" value={formData.assessmentBackupEmail} onChange={onInputChange} className={getInputClass('assessmentBackupEmail')} placeholder="backup@email.com" />
              {renderFieldError('assessmentBackupEmail')}
            </div>
            <div>
              <CountryPhoneInput
                name="assessmentBackupPhone"
                value={formData.assessmentBackupPhone}
                countryCodeValue={formData.assessmentBackupPhoneCountry}
                onChange={onPhoneChange}
                error={validationErrors.assessmentBackupPhone}
                required
                label="Phone *"
              />
            </div>
          </div>
        );

      case 3: // Assessment Details
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="col-span-2">
              <label className={labelClass}>Description *</label>
              <textarea name="assessmentDescription" value={formData.assessmentDescription} onChange={onInputChange} className={`${getInputClass('assessmentDescription')} h-24`} placeholder="Enter assessment description" />
              {renderFieldError('assessmentDescription')}
            </div>
            <div>
              <label className={labelClass}>Start Date *</label>
              <input type="date" name="assessmentStartDate" value={formData.assessmentStartDate} onChange={onInputChange} className={getInputClass('assessmentStartDate')} />
              {renderFieldError('assessmentStartDate')}
            </div>
            <div>
              <label className={labelClass}>End Date *</label>
              <input type="date" name="assessmentEndDate" value={formData.assessmentEndDate} onChange={onInputChange} className={getInputClass('assessmentEndDate')} />
              {renderFieldError('assessmentEndDate')}
            </div>
            <div>
              <label className={labelClass}>Status *</label>
              <select name="assessmentStatus" value={formData.assessmentStatus} onChange={onInputChange} className={getSelectClass('assessmentStatus')}>
                {statusOptions.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {renderFieldError('assessmentStatus')}
            </div>
            <div>
              <label className={labelClass}>Risk Rating *</label>
              <select name="assessmentRiskRating" value={formData.assessmentRiskRating} onChange={onInputChange} className={getSelectClass('assessmentRiskRating')}>
                {riskRatingOptions.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {renderFieldError('assessmentRiskRating')}
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea name="assessmentNotes" value={formData.assessmentNotes} onChange={onInputChange} className={`${getInputClass('assessmentNotes')} h-20`} placeholder="Enter notes" />
            </div>
          </div>
        );

      case 4: // Classification
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Department *</label>
              <select name="departmentId" value={formData.departmentId} onChange={onInputChange} className={getSelectClass('departmentId')}>
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d.departmentId} value={d.departmentId}>{d.departmentDesc}</option>
                ))}
              </select>
              {renderFieldError('departmentId')}
            </div>
            <div>
              <label className={labelClass}>Discipline *</label>
              <select name="disciplineId" value={formData.disciplineId} onChange={onInputChange} className={getSelectClass('disciplineId')}>
                <option value="">Select Discipline</option>
                {disciplines.map(d => (
                  <option key={d.disciplineId} value={d.disciplineId}>{d.disciplineDesc}</option>
                ))}
              </select>
              {renderFieldError('disciplineId')}
            </div>
            <div>
              <label className={labelClass}>Focus Area *</label>
              <select name="focusAreaId" value={formData.focusAreaId} onChange={onInputChange} className={getSelectClass('focusAreaId')}>
                <option value="">Select Focus Area</option>
                {focusAreas.map(f => (
                  <option key={f.focusAreaId} value={f.focusAreaId}>{f.focusAreaDesc}</option>
                ))}
              </select>
              {renderFieldError('focusAreaId')}
            </div>
            <div>
              <label className={labelClass}>Subcategory</label>
              <input type="number" name="assessmentSubcategory" value={formData.assessmentSubcategory} onChange={onInputChange} className={getInputClass('assessmentSubcategory')} placeholder="Enter subcategory" />
              {renderFieldError('assessmentSubcategory')}
            </div>
            <div>
              <label className={labelClass}>Posture</label>
              <input type="text" name="posture" value={formData.posture} onChange={onInputChange} className={getInputClass('posture')} placeholder="Enter posture" />
            </div>
          </div>
        );

      case 5: // Approvals & Completion
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Documents Approval *</label>
              <select name="documentsApproved" value={formData.documentsApproved} onChange={onInputChange} className={getSelectClass('documentsApproved')}>
                {approvalOptions.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {renderFieldError('documentsApproved')}
            </div>
            <div>
              <label className={labelClass}>Internal Approval *</label>
              <select name="internalApproval" value={formData.internalApproval} onChange={onInputChange} className={getSelectClass('internalApproval')}>
                {approvalOptions.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {renderFieldError('internalApproval')}
            </div>
            <div>
              <label className={labelClass}>External Approval *</label>
              <select name="externalApproval" value={formData.externalApproval} onChange={onInputChange} className={getSelectClass('externalApproval')}>
                {approvalOptions.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {renderFieldError('externalApproval')}
            </div>
            <div>
              <label className={labelClass}>Auditor Approval *</label>
              <select name="auditorApproval" value={formData.auditorApproval} onChange={onInputChange} className={getSelectClass('auditorApproval')}>
                {approvalOptions.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {renderFieldError('auditorApproval')}
            </div>
            <div>
              <label className={labelClass}>Auditor Opinion</label>
              <textarea name="auditorOpinion" value={formData.auditorOpinion} onChange={onInputChange} className={`${getInputClass('auditorOpinion')} h-20`} placeholder="Enter auditor opinion" />
            </div>
            <div>
              <label className={labelClass}>Exceptions</label>
              <textarea name="exceptions" value={formData.exceptions} onChange={onInputChange} className={`${getInputClass('exceptions')} h-20`} placeholder="Enter exceptions" />
            </div>
            <div>
              <label className={labelClass}>Fourth Party Exceptions</label>
              <textarea name="forthPartyExceptions" value={formData.forthPartyExceptions} onChange={onInputChange} className={`${getInputClass('forthPartyExceptions')} h-20`} placeholder="Enter fourth party exceptions" />
            </div>
            <div>
              <label className={labelClass}>Remediation Period (days) *</label>
              <input type="number" name="remediationPeriod" value={formData.remediationPeriod} onChange={onInputChange} className={getInputClass('remediationPeriod')} placeholder="Enter remediation period" />
              {renderFieldError('remediationPeriod')}
            </div>
            <div>
              <label className={labelClass}>Completion Percentage</label>
              <input type="range" name="completionPercentage" value={formData.completionPercentage} onChange={onInputChange} min="0" max="100" className="w-full" />
              <span className={`text-sm text-[#919191]`}>{formData.completionPercentage}%</span>
              {renderFieldError('completionPercentage')}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <input type="checkbox" name="completionAck" checked={formData.completionAck} onChange={onInputChange} className="w-5 h-5 rounded border-[#919191]/50" />
                <label className={`text-sm text-[#53545a]`}>Completion Acknowledged</label>
              </div>
              {renderFieldError('completionAck')}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Assessment (ID: ${selectedAssessment?.id})` : "New Assessment"}
      size="xl"
      footer={
        <div className="flex justify-between w-full">
          <div>
            {currentStep > 0 && (
              <button
                onClick={onPrevious}
                className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5`}
              >
                <FiChevronLeft /> Previous
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5`}
            >
              Cancel
            </button>
            {currentStep < steps.length - 1 ? (
              <button
                onClick={onNext}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
              >
                Next <FiChevronRight />
              </button>
            ) : (
              <button
                onClick={onSubmit}
                className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
              >
                {isEditing ? 'Save Changes' : 'Create Assessment'}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  index < currentStep
                    ? 'bg-[#0077b5] text-white'
                    : index === currentStep
                      ? 'bg-[#F47F2E] text-white'
                      : 'bg-[#919191]/30 text-[#919191]'
                }`}>
                  {index < currentStep ? <FiCheck /> : index + 1}
                </div>
                <div className="mt-2 text-center hidden sm:block">
                  <p className={`text-xs font-medium text-[#53545a]`}>{step.title}</p>
                  <p className={`text-xs text-[#919191]`}>{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 ${
                  index < currentStep
                    ? 'bg-[#0077b5]'
                    : 'bg-[#919191]/30'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className={`p-4 rounded-lg bg-[#919191]/10`}>
        {renderStepContent()}
      </div>
    </Modal>
  );
};

export default AssessmentFormModal;
